import React from 'react';
import type { FollowButtonSource } from '@soundxyz/utils/analytics';
import { shortenHash } from '@soundxyz/utils/const';
import { styled } from '@components-shared/src/stitches';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, gql, UserModalRowFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

const AVATAR_SIZE = 40;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserModalRow on User {
    id
    publicAddress
    webappUri
    username
    avatar {
      id
      url
    }
    artist {
      id
    }
  }
`);

export const UserRow = ({
  user: userFragment,
  secondaryText,
  sourceLocation,
}: {
  user: FragmentType<UserModalRowFragmentDoc>;
  secondaryText?: string;
  sourceLocation: FollowButtonSource;
}) => {
  const user = getFragment(UserModalRowFragmentDoc, userFragment);
  const { id: userId, avatar, artist, publicAddress, webappUri, username } = user;
  const name = user.username;
  const shortenedPublicAddress = shortenHash(publicAddress, 5, 4);
  const profileURL = webappUriPath(webappUri);

  let secondary = '';
  if (secondaryText) {
    secondary = secondaryText;
  } else if (name !== shortenedPublicAddress) {
    secondary = shortenedPublicAddress;
  }

  return (
    <UserContainer>
      <AccessibleLink pathnameUri={profileURL}>
        <UserInfoContainer>
          <Avatar
            size={AVATAR_SIZE}
            src={avatar?.url}
            username={username}
            walletAddress={publicAddress}
            borderRadius="50%"
            disableLink
            webappUri={webappUri}
          />
          <UserInfo>
            <NameContainer>
              <h4>{name}</h4>
              {artist && <VerifiedTooltip size={16} />}
            </NameContainer>
            {!!secondary && <h5>{secondary}</h5>}
          </UserInfo>
        </UserInfoContainer>
      </AccessibleLink>
      <FollowButton variant="primary" size="XS" userId={userId} sourceLocation={sourceLocation} />
    </UserContainer>
  );
};

const UserContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center',
  height: 52,
  paddingY: 6,
});

const UserInfoContainer = styled('div', {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginRight: 16,
});

const UserInfo = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,

  h4: {
    title: 'XXS',
    color: '$base800',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  h5: {
    base: 'S',
    color: '$base700',
  },
});

const NameContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const UserRowSkeleton = () => (
  <UserContainer className="animate-pulse">
    <UserInfoContainer>
      <AvatarSkeleton />
      <UserInfo>
        <NameSkeleton />
        <SecondarySkeleton />
      </UserInfo>
    </UserInfoContainer>
    <FollowButtonSkeleton />
  </UserContainer>
);

const AvatarSkeleton = styled(Skeleton, {
  height: AVATAR_SIZE,
  width: AVATAR_SIZE,
  borderRadius: '50%',
});

const NameSkeleton = styled(Skeleton, {
  width: 80,
  height: 16,
  borderRadius: 4,
});

const SecondarySkeleton = styled(Skeleton, {
  width: 80,
  height: 14,
  borderRadius: 4,
});

const FollowButtonSkeleton = styled(Skeleton, {
  width: 62,
  height: 26,
  borderRadius: 6,
});
